import React, { FunctionComponent } from 'react'

import {
  StyledConfirmationSection,
  StyledConsentBaselineWrapper,
  StyledIntroText,
  StyledStatusBadge,
  StyledStatusIcon,
  StyledStatusText,
  StyledStep,
  StyledStepAlert,
  StyledStepBody,
  StyledStepButton,
  StyledStepInstruction,
  StyledStepLabel,
  StyledStepLink,
  StyledStepsStack
} from './ConsentBaselineTab.styles'
import { ClinTheme } from '../../../../ClinTheme'
import { AnnounceMode } from '../../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinCheckbox } from '../../../../components/ClinCheckbox'
import { ClinIcon } from '../../../../components/ClinIcon'
import { ClinIconPathName } from '../../../../components/ClinIcon/ClinIcon.paths'
import {
  PTA_BASELINE_COMPLETE_CHECKBOX_LABEL,
  PTA_CONSENT_BASELINE_INTRO_TEXT,
  PTA_CONSENT_REFRESH_GUIDANCE
} from '../ptaConsentBaselineCompleteAnnounce'

export interface IConsentBaselineTabProps {
  /** Whether consent has been captured */
  consentCaptured: boolean
  /** Whether baseline information has been captured */
  baselineCaptured: boolean
  /** Called when the user clicks "Capture patient consent" */
  onCaptureConsent?: () => void
  /** Called when the user clicks "Capture Screening Info" */
  onSubmitBaseline?: () => void
  /** Called when the user clicks "View consent" */
  onViewConsent?: () => void
  /** Called when the user clicks "View screening info & submit followups" */
  onViewBaseline?: () => void
  /** Called when the user checks the confirmation checkbox (triggers getProgress). */
  onConfirmConsentBaseline?: () => void
  /** Whether getProgress is in flight after checkbox confirmation. */
  isConfirmingConsentBaseline?: boolean
  /** Whether getProgress has confirmed consent and baseline complete. */
  isConsentBaselineConfirmed?: boolean
  /** Error shown at the top of Step 2 when baseline verification fails. */
  baselineErrorMessage?: string
  /** Called when the user dismisses the Step 2 baseline error. */
  onDismissBaselineError?: () => void
}

interface IStepStatusBadgeProps {
  label: string
}

const StepStatusBadge: FunctionComponent<IStepStatusBadgeProps> = ({
  label
}) => (
  <StyledStatusBadge>
    <StyledStatusIcon aria-hidden>
      <ClinIcon
        iconName={ClinIconPathName.Tick}
        iconFill={ClinTheme.colors.white}
        iconSize={12}
      />
    </StyledStatusIcon>
    <StyledStatusText>{label}</StyledStatusText>
  </StyledStatusBadge>
)

interface IConsentStepProps {
  stepNumber: 1 | 2
  instruction: string
  actionLabel: string
  onAction?: () => void
  captured: boolean
  capturedLabel: string
  /** When true, render the action as a link (CLOS-13955 / CLOS-13913). */
  actionAsLink?: boolean
  /** Error rendered at the top of the step panel, above the instruction. */
  errorMessage?: string
  /** Called when the user dismisses the step error. */
  onDismissError?: () => void
  /** CLOS-13913 — content rendered inside the step panel under the action. */
  children?: React.ReactNode
}

const ConsentStep: FunctionComponent<IConsentStepProps> = ({
  stepNumber,
  instruction,
  actionLabel,
  onAction,
  captured,
  capturedLabel,
  actionAsLink = false,
  errorMessage,
  onDismissError,
  children
}) => {
  const ActionControl = actionAsLink ? StyledStepLink : StyledStepButton

  return (
    <StyledStep data-testid={`consent-baseline-step-${stepNumber}`}>
      <StyledStepLabel>Step {stepNumber}</StyledStepLabel>
      <StyledStepBody>
        {errorMessage && (
          <StyledStepAlert
            mode={AnnounceMode.Error}
            title=""
            message={errorMessage}
            iconName={ClinIconPathName.ErrorFilled}
            iconFill={ClinTheme.colors.redInvalid}
            fillRule="evenodd"
            messageClassName="pta-step-alert-message"
            isDismissible={!!onDismissError}
            handleClose={() => onDismissError?.()}
          />
        )}
        <StyledStepInstruction>{instruction}</StyledStepInstruction>
        <ActionControl
          variant={actionAsLink ? 'link' : 'purpleLight'}
          onClick={onAction}
          disabled={!onAction}
        >
          {actionLabel}
        </ActionControl>
        {captured && <StepStatusBadge label={capturedLabel} />}
        {children}
      </StyledStepBody>
    </StyledStep>
  )
}

export const ConsentBaselineTab: FunctionComponent<
  IConsentBaselineTabProps
> = ({
  consentCaptured,
  baselineCaptured,
  onCaptureConsent,
  onSubmitBaseline,
  onViewConsent,
  onViewBaseline,
  onConfirmConsentBaseline,
  isConfirmingConsentBaseline = false,
  isConsentBaselineConfirmed = false,
  baselineErrorMessage,
  onDismissBaselineError
}) => {
  const canConfirm = consentCaptured && !!onConfirmConsentBaseline

  return (
    <StyledConsentBaselineWrapper>
      <StyledIntroText>
        {/* TODO: replace with i18n key once delivered via Squidex */}
        {PTA_CONSENT_BASELINE_INTRO_TEXT}
      </StyledIntroText>

      <StyledStepsStack>
        <ConsentStep
          stepNumber={1}
          instruction="Please capture patient consent through our eConsent tool"
          actionLabel={
            consentCaptured ? 'View consent' : 'Capture patient consent'
          }
          onAction={consentCaptured ? onViewConsent : onCaptureConsent}
          captured={consentCaptured}
          capturedLabel="Patient consent captured"
          actionAsLink={consentCaptured}
        >
          {!consentCaptured && (
            <StyledStepInstruction>
              {PTA_CONSENT_REFRESH_GUIDANCE}
            </StyledStepInstruction>
          )}
        </ConsentStep>

        <ConsentStep
          stepNumber={2}
          instruction="Submit patient screening information"
          actionLabel={
            baselineCaptured
              ? 'View screening info & submit followups'
              : 'Capture Screening Info'
          }
          onAction={baselineCaptured ? onViewBaseline : onSubmitBaseline}
          captured={baselineCaptured}
          capturedLabel="Screening information captured"
          actionAsLink={baselineCaptured}
          errorMessage={baselineErrorMessage}
          onDismissError={onDismissBaselineError}
        >
          {/* CLOS-13913 — confirmation belongs inside Step 2, under the action */}
          <StyledConfirmationSection>
            <ClinCheckbox
              id="baseline-complete-confirm"
              label={PTA_BASELINE_COMPLETE_CHECKBOX_LABEL}
              checked={isConsentBaselineConfirmed}
              disabled={
                !canConfirm ||
                isConfirmingConsentBaseline ||
                isConsentBaselineConfirmed
              }
              checkBoxWidth={24}
              checkBoxHeight={24}
              onChange={(event) => {
                if (event.target.checked) {
                  onConfirmConsentBaseline?.()
                }
              }}
            />
          </StyledConfirmationSection>
        </ConsentStep>
      </StyledStepsStack>
    </StyledConsentBaselineWrapper>
  )
}
